import React from 'react';
import './AppModal.css';

// Shared modal shell mirroring the Create Brief / BriefGenerationSettingsModal design:
// dark navy header with centered title, light body with left-aligned content,
// and a footer with Cancel (outlined) + primary (orange) action buttons.
//
// Usage:
//   <AppModal
//     isOpen={open}
//     title="Merge Stories?"
//     onCancel={...}
//     onConfirm={...}
//     confirmLabel="Merge"
//     confirmVariant="primary"   // "primary" (orange) | "danger" (red)
//   >
//     ...body...
//   </AppModal>
//
// Or pass `footer` to fully replace the default footer.

function AppModal({
  isOpen,
  title,
  children,
  onCancel,
  onConfirm,
  cancelLabel = 'Cancel',
  confirmLabel = 'Continue',
  confirmVariant = 'primary',
  confirmDisabled = false,
  isSubmitting = false,
  footer,
  maxWidth = 500,
  modalTestId,
  showCancel = true,
}) {
  if (!isOpen) return null;

  const confirmBg = confirmVariant === 'danger' ? '#f44336' : '#FF6B35';

  return (
    <div className="app-modal" data-testid={modalTestId}>
      <div
        className="app-modal-content"
        style={{
          borderRadius: '12px',
          padding: 0,
          maxWidth: `${maxWidth}px`,
          margin: '0 auto',
          overflow: 'hidden',
        }}
      >
        <div className="app-modal-header">
          {/* A long title is clamped to one line by .app-modal-title; the
              tooltip keeps the full text readable. */}
          <h2 className="app-modal-title" title={typeof title === 'string' ? title : undefined}>
            {title}
          </h2>
          {/* Every modal carries a close affordance. Cancel still closes, but a
              × is what people reach for first, and a modal whose only exit is a
              button labelled for the negative action reads as a trap. */}
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              aria-label="Close"
              title="Close"
              className="app-modal-close-x"
            >
              ×
            </button>
          )}
        </div>
        <div className="app-modal-scroll">
          <div className="app-modal-body">{children}</div>
        </div>

        {footer !== undefined ? (
          <div className="app-modal-footer app-modal-footer--custom">{footer}</div>
        ) : (
          <div className="app-modal-footer">
            {showCancel ? (
              <button
                type="button"
                onClick={onCancel}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#fff',
                  color: '#3C4A5E',
                  border: '1px solid rgba(60,74,94,0.20)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  width: 'auto',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {cancelLabel}
              </button>
            ) : <span />}
            {onConfirm ? (
              <button
                type="button"
                onClick={onConfirm}
                disabled={isSubmitting || confirmDisabled}
                style={{
                  padding: '10px 24px',
                  backgroundColor: confirmBg,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (isSubmitting || confirmDisabled) ? 'not-allowed' : 'pointer',
                  width: 'auto',
                  fontSize: '14px',
                  fontWeight: 500,
                  opacity: (isSubmitting || confirmDisabled) ? 0.7 : 1,
                }}
              >
                {confirmLabel}
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

export default AppModal;
